/**
 * Tutorial System - Interactive Product Tour
 * Adapted from PRISM Chrome Extension for React/Spectra demo
 */

import emrPointer from './EMRPointer';

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: '👋 Welcome to PRISM!',
    message: 'Let\'s take a quick tour of the patient chart workflow that will save you hours each day.',
    target: null,
    position: 'center',
    showButton: true,
    duration: null
  },
  {
    id: 'status-bar',
    title: '📊 Your Command Center',
    message: 'This status bar gives you instant access to all PRISM features. It stays with you as you navigate the EMR.',
    target: '#prism-status-bar',
    position: 'top-center',
    showButton: true
  },
  // Active Problems Protocol
  {
    id: 'active-problems-icon',
    title: '🏥 Navigate to \'Active Problems\'',
    message: 'Click the Active Problems section to start documenting patient problems.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      const problemsCard = doc.querySelector('[data-testid="active_problems-card"]');
      if (problemsCard) {
        const iconButton = problemsCard.querySelector('[data-testid="active_problems-icon"]');
        return iconButton || problemsCard;
      }
      return null;
    }
  },
  {
    id: 'add-problem-button',
    title: '➕ Click \'Add Problem\' button',
    message: 'Click the Add Problem button to open the problem entry form.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      return doc.querySelector('button[data-testid="add-active_problems-button"]') ||
             doc.querySelector('button[aria-label*="Add Problem"]');
    }
  },
  {
    id: 'save-problem-button',
    title: '💾 Save Problem',
    message: 'Fill out the problem details in the form, then click this button to save.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      return doc.querySelector('button[type="submit"]') ||
             doc.querySelector('button[aria-label*="Save Problem"]');
    }
  },
  // Medications Protocol
  {
    id: 'medications-icon',
    title: '💊 Navigate to \'Medications\'',
    message: 'Click the Medications section to manage patient medications.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      const medsCard = doc.querySelector('[data-testid="medications-card"]');
      if (medsCard) {
        const iconButton = medsCard.querySelector('[data-testid="medications-icon"]');
        return iconButton || medsCard;
      }
      return null;
    }
  },
  {
    id: 'add-medication-button',
    title: '➕ Click \'Add Medication\' button',
    message: 'Click the Add Medication button to open the medication entry form.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      return doc.querySelector('button[data-testid="add-medications-button"]') ||
             doc.querySelector('button[aria-label*="Add Medication"]');
    }
  },
  {
    id: 'fill-medication-form',
    title: '💊 Save Medication',
    message: 'Fill out the medication details in the form, then click this button to save.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      return doc.querySelector('button[type="submit"]') ||
             doc.querySelector('button[aria-label*="Submit"]');
    }
  },
  // Clinical Notes Protocol
  {
    id: 'clinical-notes-icon',
    title: '📝 Navigate to \'Clinical Notes\'',
    message: 'Click the Clinical Notes section to document patient notes.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      const notesCard = doc.querySelector('[data-testid="clinical_notes-card"]');
      if (notesCard) {
        const iconButton = notesCard.querySelector('[data-testid="clinical_notes-icon"]');
        return iconButton || notesCard;
      }
      return null;
    }
  },
  {
    id: 'add-note-button',
    title: '➕ Click \'Add Note\' button',
    message: 'Click the Add Note button to create a new clinical note.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      return doc.querySelector('button[data-testid="add-clinical_notes-button"]') ||
             doc.querySelector('button[aria-label*="Add Note"]');
    }
  },
  {
    id: 'fill-note-form',
    title: '📝 Save Note',
    message: 'Fill out the note details in the form, then click this button to save.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      return doc.querySelector('button[type="submit"]') ||
             doc.querySelector('button[aria-label*="Save Note"]');
    }
  },
  {
    id: 'protocols',
    title: '📚 Protocol Discovery',
    message: 'Click here or press <strong>Ctrl+K then P</strong> to instantly search for clinical protocols. Can save 5+ minutes per chart!',
    target: 'button[aria-label*="Search Protocols"]',
    position: 'top-center',
    showButton: true
  },
  {
    id: 'ai-notes',
    title: '🤖 AI Documentation Assistant',
    message: 'Generate clinical notes based on your chart activity. Press <strong>Ctrl+K then D</strong> to create smart documentation.',
    target: 'button[aria-label*="Documentation Assistant"]',
    position: 'top-center',
    showButton: true
  },
  {
    id: 'timer',
    title: '⏱️ Chart Timer',
    message: 'Track time spent in each chart. PRISM helps you stay efficient with gentle reminders at 5, 10, and 15 minutes.',
    target: null,
    position: 'top-center',
    showButton: true,
    customSelector: (doc) => {
      const statusBar = doc.querySelector('#prism-status-bar');
      if (statusBar) {
        const boxes = statusBar.querySelectorAll('div');
        for (const box of boxes) {
          const text = box.textContent?.trim();
          if (text && /^\d+:\d{2}$/.test(text)) {
            return box.parentElement;
          }
        }
      }
      return null;
    }
  },
  {
    id: 'complete',
    title: '🎉 You\'re All Set!',
    message: 'You\'ve learned the complete patient charting workflow! You can replay this tutorial anytime from the Platform Updates menu.<br/><br/>Happy charting! 💙',
    target: null,
    position: 'center',
    showButton: true,
    finalStep: true
  }
];

class TutorialSystem {
  constructor() {
    this.currentStep = 0;
    this.active = false;
    this.steps = TUTORIAL_STEPS;
    this.overlay = null;
  }

  /**
   * Start the tutorial
   */
  start() {
    console.log('[TutorialSystem] Starting tutorial');
    this.active = true;
    this.currentStep = 0;
    this.showStep(0);
  }

  /**
   * Stop the tutorial
   */
  stop() {
    console.log('[TutorialSystem] Stopping tutorial');
    this.active = false;
    this.currentStep = 0;
    emrPointer.clear();
    this.removeOverlay();
  }

  /**
   * Show a specific tutorial step
   * @param {number} stepIndex - Step index to show
   */
  showStep(stepIndex) {
    if (stepIndex >= this.steps.length) {
      this.stop();
      return;
    }

    const step = this.steps[stepIndex];
    console.log('[TutorialSystem] Showing step', stepIndex + 1, 'of', this.steps.length, step);

    // Clear previous pointer
    emrPointer.clear();

    // Find target element
    let target = null;
    if (step.customSelector) {
      target = step.customSelector(document);
    } else if (step.target) {
      target = document.querySelector(step.target);
    }

    // For steps without targets (like welcome/complete), create a centered dummy element
    if (!target) {
      target = document.createElement('div');
      target.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 1px;
        height: 1px;
        pointer-events: none;
        z-index: 1;
      `;
      document.body.appendChild(target);
      // Store reference for cleanup
      this._centerTarget = target;
    }

    // Show pointer to target (or centered dummy) for ALL steps
    emrPointer.pointTo(
      target,
      step.title,
      step.message,
      {
        position: step.position || 'top-center',
        showButton: step.showButton !== false,
        onClose: () => {
          // Clean up center target if it exists
          if (this._centerTarget) {
            this._centerTarget.remove();
            this._centerTarget = null;
          }
          
          this.currentStep++;
          setTimeout(() => {
            this.showStep(this.currentStep);
          }, 300);
        }
      }
    );
  }

  /**
   * Remove overlay and clean up center targets
   */
  removeOverlay() {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    // Clean up center target if it exists
    if (this._centerTarget) {
      this._centerTarget.remove();
      this._centerTarget = null;
    }
  }

  /**
   * Check if tutorial should auto-start
   * @param {URLSearchParams} urlParams - URL parameters
   * @returns {boolean} Should start tutorial
   */
  shouldAutoStart(urlParams) {
    return urlParams.get('tutorial') === '1' || urlParams.get('tour') === '1';
  }
}

// Create singleton instance
const tutorialSystem = new TutorialSystem();

// Expose globally for debugging
if (typeof window !== 'undefined') {
  window.prismTutorial = tutorialSystem;
}

export default tutorialSystem;
